import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { calculatePolygonArea } from '@/editor/model/polygon/utils';
import { IWallEdge } from '@/editor/types';
import { getWallsString } from '../room';
import { getCorePolygon, getPolygonOverlapStats } from '../room/match';

export interface IRegionCoreArea {
  corePathInfos: IWallEdge[]; // 壁芯外环
  coreHoleInfos: IWallEdge[][]; // 壁芯洞环
}

export interface IRegionMatchResult {
  regionId?: string;
  // true: 壁芯墙集合完全一致；false: 靠兜底认领(墙集与上一轮算出来的不一致)
  exact: boolean;
}

export interface IRegionMatchCandidate {
  id: string;
  relateSeparateWallIds: string[];
  /** 上一轮算出来的壁芯外环。存档里有，coverDatas 会保留；更老的存档没有时为空，第三轮自动跳过 */
  corePathInfos?: IWallEdge[];
  /** 壁芯洞环。coverDatas 会把它清空，因此存档加载后的首次匹配里恒为空 */
  coreHoleInfos?: IWallEdge[][];
}

// 第三轮兜底的门槛：新旧壁芯轮廓的重合度(交/并)。
// 房间那边取 0.7，因为它兜的形态是「存档少记了绕行边、真的挖掉了一块」；
// 分区这边兜的是「隔墙 id 变了、地块一点没动」，所以顶到 0.98 —— 只认几乎完全重合的，
// 把分区被拆分/合并挡在外面(拆成两半时两半的重合度加起来才 1，谁都到不了 0.98)。
const MIN_OVERLAP_RATIO = 0.98;
// 重合度的可信下限：新区域在采样网格里至少要占到这么多个格子。
// 样本只有个位数时(区域相对包围盒极小)这个比值不可信，宁可不认。
const MIN_OVERLAP_SAMPLES = 16;

// 分区身份用的墙集：外环 + 洞环上所有隔墙 id 去重（与 RegionStore 里 relateSwIds 的算法保持一致）
export function getAreaSepIds(area: IRegionCoreArea): string[] {
  // 壁芯边必然带 sepId；缺失的边跳过，避免 undefined 混进身份用的墙集
  const pathWallIds = area.corePathInfos.flatMap((edge) => (edge.sepId ? [edge.sepId] : []));
  const holeWallIds = area.coreHoleInfos.flatMap((edges) => edges.flatMap((edge) => (edge.sepId ? [edge.sepId] : [])));
  return Array.from(new Set([...pathWallIds, ...holeWallIds]));
}

// 两条边是不是同一段几何、方向相反
function isReverseEdge(a: IWallEdge, b: IWallEdge): boolean {
  // 一条是弧一条是直线，不可能互为反向
  if (!!a.center !== !!b.center) return false;
  if (!Point2dUtils.equals(a.start, b.end) || !Point2dUtils.equals(a.end, b.start)) return false;
  if (a.center && b.center) {
    if (!Point2dUtils.equals(a.center, b.center)) return false;
    // 端点互换的同心弧还要方向相反，否则是同一段弧的另一半(优弧/劣弧)
    if (a.clockwise === b.clockwise) return false;
  }
  return true;
}

// 组内的边能不能两两配成反向对。
// 组是按「同一个环、同一堵墙」分的，组里的边要么是方向 d 要么是 -d，
// 贪心配对在这种结构上就是精确解：配不上说明两个方向的条数不相等，完美匹配本来就不存在。
function canPairAllEdges(edges: IWallEdge[]): boolean {
  if (edges.length === 0 || edges.length % 2 !== 0) return false;

  const used = edges.map(() => false);
  for (let i = 0; i < edges.length; i++) {
    if (used[i]) continue;

    const partner = edges.findIndex((edge, j) => j > i && !used[j] && isReverseEdge(edges[i], edge));
    if (partner === -1) return false;

    used[i] = true;
    used[partner] = true;
  }
  return true;
}

/**
 * 找出这个壁芯区域里「零面积」的隔墙 —— 在同一个环里被正反各走了一遍的支墙。
 *
 * 一堵墙搭在区域边界上、另一端挂着个小盒子时，壁芯环会沿它的中线走过去、绕盒子一圈、
 * 再沿同一条中线走回来（本例 h30zFBJH4NFwPpxv / kUC12glISlOioxCR 就是这个形态）。
 * 这一对边在面积积分里正负抵消：把这堵墙从环里拿掉，围出来的地块一点没变
 * —— 只是原来是「外环 + 一个洞」，现在是「一个自接触的环」。
 *
 * 所以这类墙进不进 relateSeparateWallIds，描述的都是同一个分区，可以拿来做兜底认领的依据。
 *
 * 配对必须在同一个环内做：同一堵墙一次出现在外环、一次出现在洞环时是真边界（洞挨着外轮廓），
 * 不是零面积支墙，跨环配对会把它误判成可忽略的。
 */
export function getDegenerateSepIds(area: IRegionCoreArea): Set<string> {
  const rings = [area.corePathInfos, ...area.coreHoleInfos];

  // sepId -> 每个环里该墙的边；只要有一个环里配不成对，这堵墙就不算零面积
  const ringGroups: Map<string, IWallEdge[]>[] = rings.map((ring) => {
    const group = new Map<string, IWallEdge[]>();
    ring.forEach((edge) => {
      if (!edge.sepId) return;

      const edges = group.get(edge.sepId);
      if (edges) edges.push(edge);
      else group.set(edge.sepId, [edge]);
    });
    return group;
  });

  const allSepIds = new Set(ringGroups.flatMap((group) => Array.from(group.keys())));
  const degenerateSepIds = new Set<string>();
  allSepIds.forEach((sepId) => {
    const groups = ringGroups.map((group) => group.get(sepId)).filter((edges) => !!edges);
    if (groups.every((edges) => canPairAllEdges(edges))) degenerateSepIds.add(sepId);
  });

  return degenerateSepIds;
}

/**
 * 为每个新壁芯区域找出它对应的旧分区(返回与 areas 等长的数组，认领不到时 regionId 为 undefined)。
 *
 * 三轮匹配，每一轮都只认「能证明是同一块地」的：
 *  1. 壁芯墙集合(排序去重后的 sepId 串)完全一致 —— 原有逻辑，命中即消费(同一个 key 只认领一次)；
 *  2. 差集全是零面积支墙 —— 从墙集合本身证明等价；
 *  3. 壁芯轮廓几乎完全重合 —— 与房间那边(matchRoomsByCorePath)同款，按几何认。
 *
 * 第二轮治的是：旧版本存下来的 relateSeparateWallIds 不含「搭在边界上、环要沿它出去再绕回来」的支墙
 * (现场：前室 31 堵 vs 现在算出来的 33 堵)，墙集 key 天然对不上，但描述的是同一块地。判据：
 *   ① 旧分区的每一堵墙都必须还在新区域的墙集里(旧 ⊆ 新)；
 *   ② 多出来的那几堵(新 \ 旧)必须全是零面积支墙。
 * 两条合起来等价于「新区域里真正围合出面积的墙 ⊆ 旧墙集 ⊆ 新墙集」。① 把分区被拆分/合并、
 * 墙被删改全挡在外面(拆出来的每一半都会带上那堵新的分隔墙，① 就过不了；合并后消失的分隔墙不在新集里，
 * ① 同样过不了)。② 保证差集对面积零贡献。
 *
 * 第三轮治的是：基础隔墙的 id 是按房间轮廓的点哈希(startId/endId)认领的，边界墙的接头拓扑一变
 * (现场：主墙 qqUNd5uf1CsAGMD9 被拆成两段)，同一段边界就会被删掉、重建成一个新 id ——
 * 分区的墙集里凭空多出死 id，前两轮都对不上，但它围的还是同一块地。墙集这条线索已经断了，
 * 只能像房间那样按壁芯轮廓的重合度认。门槛顶到 0.98(房间那边是 0.7)，只认几何几乎没动的。
 *
 * 每一轮都不碰前面已经认领走的分区；配对唯一，有歧义宁可放弃、保持删掉重建。
 * 第一轮的行为与原逻辑逐字等价。
 */
export function matchRegionsBySepWalls(
  areas: IRegionCoreArea[],
  regions: IRegionMatchCandidate[]
): IRegionMatchResult[] {
  const matchResults: IRegionMatchResult[] = areas.map(() => ({ exact: false }));
  const areaSepIdsList = areas.map((area) => getAreaSepIds(area));

  // ===== 第一轮：壁芯墙集合精确匹配 =====
  // 与原逻辑逐字等价：重复 key 后者覆盖前者(new Map 的语义)，命中后立刻从表里删掉
  // —— 后面再算出同一个 key 的区域走新建分支。不要改成可重复命中，那会改变既有行为。
  const regionKeyMap = new Map<string, string>();
  regions.forEach((region) => regionKeyMap.set(getWallsString(region.relateSeparateWallIds), region.id));

  const usedRegionIds = new Set<string>();
  areas.forEach((_, index) => {
    const key = getWallsString(areaSepIdsList[index]);
    const regionId = regionKeyMap.get(key);
    if (!regionId) return;

    regionKeyMap.delete(key);
    usedRegionIds.add(regionId);
    matchResults[index] = { regionId, exact: true };
  });

  // 只认一一对应的配对：有歧义就全部放弃(保持删掉重建)，不去猜哪个更像
  const claimUniquePairs = (pairs: { index: number; regionId: string }[]) => {
    const areaHitCount = new Map<number, number>();
    const regionHitCount = new Map<string, number>();
    pairs.forEach(({ index, regionId }) => {
      areaHitCount.set(index, (areaHitCount.get(index) ?? 0) + 1);
      regionHitCount.set(regionId, (regionHitCount.get(regionId) ?? 0) + 1);
    });
    pairs.forEach(({ index, regionId }) => {
      if (areaHitCount.get(index) !== 1 || regionHitCount.get(regionId) !== 1) return;

      usedRegionIds.add(regionId);
      matchResults[index] = { regionId, exact: false };
    });
  };

  const getPendingAreas = () =>
    areas.map((area, index) => ({ area, index })).filter(({ index }) => !matchResults[index].regionId);
  const getFreeRegions = () => regions.filter((region) => !usedRegionIds.has(region.id));

  // ===== 第二轮：差集全是零面积支墙时兜底认领 =====
  // 必须等第一轮全部结束再做，否则前面某个区域可能抢走后面能精确匹配上的分区。
  const pendingAreas = getPendingAreas();
  const freeRegions = getFreeRegions();
  if (!pendingAreas.length || !freeRegions.length) return matchResults;

  const pairs: { index: number; regionId: string }[] = [];
  pendingAreas.forEach(({ area, index }) => {
    const newSepIds = new Set(areaSepIdsList[index]);
    if (!newSepIds.size) return;

    const degenerateSepIds = getDegenerateSepIds(area);
    // 一堵围合的墙都没有，谈不上是哪个分区(正常的环不会走到这)
    if (degenerateSepIds.size >= newSepIds.size) return;

    freeRegions.forEach((region) => {
      const oldSepIds = region.relateSeparateWallIds;
      if (!oldSepIds.length) return;

      // ① 旧分区的每一堵墙都还在新区域的墙集里
      if (!oldSepIds.every((sepId) => newSepIds.has(sepId))) return;

      // ② 多出来的那几堵必须全是零面积支墙；差集为空的话第一轮就命中了，走到这说明数据有问题
      const oldSepIdSet = new Set(oldSepIds);
      const extraSepIds = Array.from(newSepIds).filter((sepId) => !oldSepIdSet.has(sepId));
      if (!extraSepIds.length || !extraSepIds.every((sepId) => degenerateSepIds.has(sepId))) return;

      pairs.push({ index, regionId: region.id });
    });
  });

  // ③ 只认一一对应的配对
  claimUniquePairs(pairs);

  // ===== 第三轮：壁芯轮廓几乎完全重合时兜底认领(隔墙 id 被换掉了) =====
  // 与房间那边 matchRoomsByCorePath 的第二轮同一套：把壁芯环离散成多边形，按网格采样估重合度。
  // 不能用多边形布尔运算 —— 壁芯环遇到「搭在边界上的墙」会沿同一条中线出去再回来，形成零宽缝，
  // polybool 在这种自接触环上会把缝两侧算重(详见 utils/room/match.ts 的注释)。
  const geometryCandidates = getFreeRegions()
    .map((region) => {
      const polygon = getCorePolygon(region.corePathInfos ?? []);
      return {
        id: region.id,
        polygon,
        size: Math.abs(calculatePolygonArea(polygon)),
        holeCount: region.coreHoleInfos?.length ?? 0
      };
    })
    // 更老的存档没存壁芯几何 —— 认不了，跳过
    .filter((candidate) => candidate.polygon.length >= 3 && candidate.size > 0);

  const geometryPairs: { index: number; regionId: string }[] = [];
  getPendingAreas().forEach(({ area, index }) => {
    const polygon = getCorePolygon(area.corePathInfos);
    if (polygon.length < 3) return;

    const size = Math.abs(calculatePolygonArea(polygon));
    if (!size) return;

    geometryCandidates.forEach((candidate) => {
      // ① 洞的个数必须一样。重合度只看外环，多/少一个洞外环照样重合 —— 那是真的多/少了一块地
      if (candidate.holeCount !== area.coreHoleInfos.length) return;

      // ② 便宜的预筛：重合度 ≤ 小面积/大面积
      if (Math.min(size, candidate.size) / Math.max(size, candidate.size) < MIN_OVERLAP_RATIO) return;

      // ③ 采样估重合度，样本太少不算数
      const { iou, aSamples } = getPolygonOverlapStats(polygon, candidate.polygon);
      if (iou < MIN_OVERLAP_RATIO || aSamples < MIN_OVERLAP_SAMPLES) return;

      geometryPairs.push({ index, regionId: candidate.id });
    });
  });
  claimUniquePairs(geometryPairs);

  return matchResults;
}
